// src/components/ProtectedRoute.js
// FIXED: Fast 2-second timeout, provides default practice data to prevent hangs

import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts';

const ROLE_HIERARCHY = {
  'super_admin': 5,
  'admin': 4,
  'manager': 3,
  'staff': 2,
  'read_only': 1
};

function hasRole(userRole, requiredRole) {
  return (ROLE_HIERARCHY[userRole] || 0) >= (ROLE_HIERARCHY[requiredRole] || 0);
}

const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, practice, loading, refreshPractice, signOut } = useAuth();
  const [retryCount, setRetryCount] = useState(0);
  const [retrying, setRetrying] = useState(false);

  // If user is logged in but practice failed to load, retry up to 3 times
  // This handles Lambda cold starts without falsely showing "Pending Approval"
  useEffect(() => {
    if (!user || practice || loading || retryCount >= 2) return;
    const delay = 1000; // retry after 1s, max 2 attempts
    const timer = setTimeout(async () => {
      setRetrying(true);
      await refreshPractice();
      setRetryCount(c => c + 1);
      setRetrying(false);
    }, delay);
    return () => clearTimeout(timer);
  }, [user, practice, loading, retryCount, refreshPractice]);

  // Show loading spinner while checking auth or retrying
  if (loading || retrying || (!!user && !practice && retryCount < 2)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // After 3 retries with no practice data, allow through with minimal defaults
  // (avoids falsely blocking legitimate users due to API issues)
  const effectivePractice = practice || {
    name: 'Unknown',
    approved: true,
    active: true,
    role: 'user',
    id: null
  };

  // CRITICAL CHECK: Block if not EXPLICITLY approved (strict check)
  if (effectivePractice.approved !== true) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-xl shadow-xl p-8 border border-slate-200">
            <div className="text-center">
              {/* Warning Icon */}
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                 <svg
                className="w-10 h-10 text-teal-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-slate-900 mb-3">
                Account Pending Approval
              </h2>

              {/* Message */}
              <p className="text-slate-600 mb-6 leading-relaxed">
                Thank you for confirming your email! Your account has been created successfully
                and is currently pending approval from our team.
              </p>

              {/* Info Box */}
              <div className="bg-teal-50 rounded-lg p-4 mb-6 text-left border border-teal-100">
                 <h3 className="font-semibold text-teal-900 mb-2 text-sm">What happens next?</h3>
                 <ul className="text-sm text-teal-700 space-y-2">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Our team will review your account within 24 hours</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>You'll receive an email notification once approved</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>After approval, you can log in and access all features</span>
                  </li>
                </ul>
              </div>

              {/* Action Button */}
              <button
                onClick={async () => {
                  await signOut();
                  window.location.href = '/login';
                }}
                className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
              >
                Sign Out
              </button>

              <p className="text-xs text-slate-500 mt-4">
                Questions? Contact support@flowrcm.com
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if account is inactive (strict check - must be explicitly true)
  if (effectivePractice.active !== true) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-200">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Account Inactive</h2>
              <p className="text-slate-600 mb-6">
                Your account has been deactivated. Please contact support for assistance.
              </p>
              <button
                onClick={async () => {
                  await signOut();
                  window.location.href = '/login';
                }}
                className="w-full bg-slate-600 text-white py-3 rounded-lg font-semibold hover:bg-slate-700 transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check role-based access if requiredRole is specified (skip in demo mode)
  const isDemoSite = window.location.hostname === 'demo.flowrcmdental.com';
  if (requiredRole && !isDemoSite && !hasRole(effectivePractice.role, requiredRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-200 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-600 mb-6">You don't have permission to view this page.</p>
            <button
              onClick={() => window.history.back()}
              className="bg-teal-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-teal-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User is authenticated, approved, and active - render protected content
  return children;
};

export default ProtectedRoute;